(function () {
  'use strict';

  const MODEL_VIEWER_SRC = 'https://unpkg.com/@google/model-viewer@4.2.0/dist/model-viewer.min.js';
  let loaderPromise = null;

  function parseConfig(root) {
    if (!root) return null;
    const node = root.querySelector('[data-lumira-3d-config]');
    if (!node) return null;

    try {
      const config = JSON.parse(node.textContent || '{}');
      if (!config || !config.model || !config.model.src) return null;
      return config;
    } catch (error) {
      console.warn('[Lumira 3D] Invalid viewer config', error);
      return null;
    }
  }

  function loadModelViewer() {
    if (window.customElements && customElements.get('model-viewer')) {
      return Promise.resolve();
    }

    function waitForDefinition() {
      if (!window.customElements || !customElements.whenDefined) return Promise.resolve();
      return customElements.whenDefined('model-viewer');
    }

    if (!loaderPromise) {
      loaderPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-lumira-model-viewer-loader]');
        if (existing) {
          if (existing.dataset.loaded === 'true') {
            waitForDefinition().then(resolve).catch(reject);
            return;
          }
          existing.addEventListener('load', () => {
            existing.dataset.loaded = 'true';
            waitForDefinition().then(resolve).catch(reject);
          }, { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }

        const script = document.createElement('script');
        script.type = 'module';
        script.src = MODEL_VIEWER_SRC;
        script.dataset.lumiraModelViewerLoader = 'true';
        script.addEventListener('load', () => {
          script.dataset.loaded = 'true';
          waitForDefinition().then(resolve).catch(reject);
        }, { once: true });
        script.addEventListener('error', reject, { once: true });
        document.head.appendChild(script);
      });
    }

    return loaderPromise;
  }

  function normalizeText(value) {
    return String(value || '').trim().toLowerCase().replace(/[-_/]+/g, ' ').replace(/\s+/g, ' ');
  }

  function inferHexColor(label) {
    const value = normalizeText(label);
    const palette = [
      ['white', '#f6f3ed'],
      ['ivory', '#ede4d4'],
      ['cream', '#e8dac2'],
      ['beige', '#c9b99d'],
      ['sand', '#bca98c'],
      ['taupe', '#9a8873'],
      ['gray', '#8f9290'],
      ['grey', '#8f9290'],
      ['charcoal', '#373737'],
      ['black', '#161616'],
      ['brown', '#6b4a32'],
      ['walnut', '#694426'],
      ['espresso', '#33251b'],
      ['oak', '#b68a58'],
      ['natural', '#c6a174'],
      ['blue', '#536e8e'],
      ['navy', '#1e3150'],
      ['green', '#63755d'],
      ['olive', '#6d7452'],
      ['red', '#8f3a32'],
      ['burgundy', '#652d34']
    ];

    const hit = palette.find(([name]) => value.includes(name));
    return hit ? hit[1] : '#b8a27f';
  }

  function hexToFactor(hex) {
    const match = String(hex || '').replace('#', '').match(/^([0-9a-f]{6})$/i);
    if (!match) return null;
    const intValue = parseInt(match[1], 16);
    return [
      ((intValue >> 16) & 255) / 255,
      ((intValue >> 8) & 255) / 255,
      (intValue & 255) / 255,
      1
    ];
  }

  function collectColorOptions(config) {
    const names = ['color', 'colour', 'finish', 'fabric', 'material'];
    const options = Array.isArray(config.options) ? config.options : [];
    const option = options.find((item) => names.some((name) => normalizeText(item.name).includes(name)));

    if (option && Array.isArray(option.values) && option.values.length) {
      return option.values.map((name) => ({
        name,
        hex: inferHexColor(name),
        optionName: option.name
      }));
    }

    return [
      { name: 'Ivory', hex: '#ede4d4', optionName: 'Color' },
      { name: 'Taupe', hex: '#9a8873', optionName: 'Color' },
      { name: 'Charcoal', hex: '#373737', optionName: 'Color' }
    ];
  }

  function collectPieceOptions(config) {
    const variants = Array.isArray(config.variants) ? config.variants : [];
    const found = new Set();
    const sources = variants.map((variant) => (
      [variant.title, variant.option1, variant.option2, variant.option3].join(' ')
    ));
    sources.push(config && config.product && config.product.title);
    sources.forEach((source) => {
      const text = normalizeText(source);
      const pattern = /\b(\d{1,2})\s*(?:pc|piece)\b/g;
      let match;
      while ((match = pattern.exec(text)) !== null) {
        found.add(`${Number(match[1])}pc`);
      }
    });

    return Array.from(found).sort((left, right) => parseInt(left, 10) - parseInt(right, 10));
  }

  function findVariant(config, state) {
    const variants = Array.isArray(config.variants) ? config.variants : [];
    if (!variants.length) return null;

    const color = normalizeText(state.color);
    const pieces = normalizeText(state.pieces);
    const exact = variants.find((variant) => {
      const text = normalizeText([variant.title, variant.option1, variant.option2, variant.option3].join(' '));
      const pieceNumber = pieces.replace('pc', '');
      const colorOk = !color || text.includes(color);
      const pieceOk = !pieces || text.includes(pieces) || text.includes(`${pieceNumber} pc`) || text.includes(`${pieceNumber} piece`);
      return colorOk && pieceOk && variant.available !== false;
    });

    return exact || variants.find((variant) => variant.id === config.selectedVariantId) || variants[0];
  }

  function applyModelColor(viewer, hex) {
    const factor = hexToFactor(hex);
    if (!viewer || !factor) return false;

    try {
      const model = viewer.model;
      if (!model || !Array.isArray(model.materials)) return false;
      model.materials.forEach((material) => {
        const pbr = material && material.pbrMetallicRoughness;
        if (pbr && typeof pbr.setBaseColorFactor === 'function') {
          pbr.setBaseColorFactor(factor);
        }
      });
      return true;
    } catch (error) {
      console.warn('[Lumira 3D] Could not update material color', error);
      return false;
    }
  }

  function applyLighting(viewer, mode) {
    if (!viewer) return;
    const settings = {
      studio: { exposure: '1', shadow: '0.72' },
      warm: { exposure: '1.12', shadow: '0.86' },
      cool: { exposure: '0.94', shadow: '0.62' }
    }[mode] || { exposure: '1', shadow: '0.72' };

    viewer.setAttribute('environment-image', 'neutral');
    viewer.setAttribute('exposure', settings.exposure);
    viewer.setAttribute('shadow-intensity', settings.shadow);
  }

  function setBackground(root, mode, roomImage) {
    if (!root) return;
    root.dataset.lumiraBackground = mode;
    if (roomImage) {
      root.style.setProperty('--lumira-room-image', `url("${roomImage}")`);
    }
  }

  function isMobileARDevice() {
    const ua = navigator.userAgent || '';
    return /Android|iPhone|iPad|iPod/i.test(ua);
  }

  function productUrl(config) {
    const handleUrl = config && config.product && config.product.url;
    if (!handleUrl) return window.location.href;
    return new URL(handleUrl, window.location.origin).href;
  }

  function renderQrFallback(root, config) {
    const fallback = root && root.querySelector('[data-lumira-3d-ar-fallback]');
    const img = root && root.querySelector('[data-lumira-3d-qr]');
    if (!fallback || !img) return;
    const url = productUrl(config);
    img.src = `https://api.qrserver.com/v1/create-qr-code/?size=176x176&margin=12&data=${encodeURIComponent(url)}`;
    fallback.hidden = isMobileARDevice();
  }

  function zoom(viewer, direction) {
    if (!viewer) return;
    const current = String(viewer.getAttribute('camera-orbit') || viewer.cameraOrbit || '20deg 68deg auto');
    const parts = current.split(/\s+/);
    const radius = parts[2] || 'auto';
    let nextRadius = radius;
    const numeric = parseFloat(radius);
    if (Number.isFinite(numeric)) {
      const unit = radius.replace(String(numeric), '') || 'm';
      nextRadius = `${Math.max(0.4, numeric * (direction === 'in' ? 0.82 : 1.18)).toFixed(2)}${unit}`;
    } else {
      viewer.fieldOfView = direction === 'in' ? '22deg' : '38deg';
    }
    viewer.cameraOrbit = `${parts[0] || '20deg'} ${parts[1] || '68deg'} ${nextRadius}`;
  }

  function feetToScene(value) {
    return Number(value || 0) * 0.3048;
  }

  function createComposerScene(canvas, room) {
    if (!window.THREE || !canvas) return null;
    const THREE = window.THREE;
    const dims = room && room.estimated_dimensions ? room.estimated_dimensions : {};
    const width = feetToScene(dims.width_ft || 14);
    const depth = feetToScene(dims.depth_ft || 16);
    const height = feetToScene(dims.height_ft || 9);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xfafaf8);
    applyCleanedRoomBackground(scene, room);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;

    const camera = new THREE.PerspectiveCamera(48, 1, 0.05, 120);
    camera.position.set(width * 0.55, height * 0.72, depth * 0.95);

    const controls = window.THREE.OrbitControls ? new window.THREE.OrbitControls(camera, canvas) : null;
    if (controls) {
      controls.target.set(0, height * 0.35, 0);
      controls.enableDamping = true;
      controls.maxPolarAngle = Math.PI * 0.48;
    }

    const ambient = new THREE.HemisphereLight(0xfff4df, 0x9da7aa, 1.2);
    scene.add(ambient);
    const sun = new THREE.DirectionalLight(0xffffff, 1.8);
    sun.position.set(width * 0.35, height * 1.6, depth * 0.42);
    sun.castShadow = true;
    scene.add(sun);

    const roomGroup = new THREE.Group();
    roomGroup.name = 'room-shell';
    scene.add(roomGroup);
    buildRoomShell(roomGroup, room);

    const state = {
      scene,
      renderer,
      camera,
      controls,
      roomGroup,
      selectable: [],
      placed: [],
      humans: [],
      selected: null,
      room,
      width,
      depth,
      height,
    };

    function resize() {
      const rect = canvas.getBoundingClientRect();
      const w = Math.max(320, rect.width || canvas.clientWidth || 800);
      const h = Math.max(280, rect.height || canvas.clientHeight || 600);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }

    function frame() {
      resize();
      if (controls) controls.update();
      renderer.render(scene, camera);
      state.raf = window.requestAnimationFrame(frame);
    }

    resize();
    frame();
    return state;
  }

  function buildRoomShell(group, room) {
    if (!window.THREE || !group) return;
    const THREE = window.THREE;
    while (group.children.length) group.remove(group.children[0]);
    const dims = room && room.estimated_dimensions ? room.estimated_dimensions : {};
    const width = feetToScene(dims.width_ft || 14);
    const depth = feetToScene(dims.depth_ft || 16);
    const height = feetToScene(dims.height_ft || 9);
    const floorData = room && room.floor ? room.floor : {};
    const ceilingData = room && room.ceiling ? room.ceiling : {};
    const trim = room && room.trim ? room.trim : {};
    const floorColor = colorValue(floorData.color_hex, floorColorFor(room && room.floor_type));
    const wallColor = wallColorFor(room && room.color_palette);

    const floorMat = new THREE.MeshStandardMaterial({ color: floorColor, roughness: 0.72, metalness: 0.02 });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    floor.name = 'floor';
    group.add(floor);
    addFloorTexture(group, width, depth, room);

    const walls = normalizeWalls(room, width, depth, wallColor);
    walls.forEach((wall) => addWall(group, wall, width, depth, height));

    const ceiling = new THREE.Mesh(
      new THREE.PlaneGeometry(width, depth),
      new THREE.MeshStandardMaterial({ color: colorValue(ceilingData.color_hex, 0xf6f2ea), roughness: 0.9 })
    );
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.y = height;
    group.add(ceiling);

    addBaseboards(group, walls, width, depth, trim);
    (Array.isArray(room && room.openings) ? room.openings : []).forEach((opening) => addOpening(group, opening, width, depth, height));
    addCeilingFeatures(group, room, width, depth, height);
    addGridLines(group, width, depth);
    addCleanedRoomBackdrop(group, room, width, depth, height);
  }

  function roomBackdropUrl(room) {
    return (room && (room.room_backdrop_url || room.cleaned_image_url || (room.render_hints && room.render_hints.backdrop_url))) || '';
  }

  function applyCleanedRoomBackground(scene, room) {
    if (!window.THREE || !scene) return;
    const url = roomBackdropUrl(room);
    if (!url) return;
    const THREE = window.THREE;
    new THREE.TextureLoader().load(url, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace || texture.colorSpace;
      scene.background = texture;
    });
  }

  function addCleanedRoomBackdrop(group, room, width, depth, height) {
    if (!window.THREE || !group) return;
    const url = roomBackdropUrl(room);
    if (!url) return;
    const THREE = window.THREE;
    const loader = new THREE.TextureLoader();
    loader.load(url, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace || texture.colorSpace;
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.82
      });
      const backdrop = new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
      backdrop.name = 'cleaned-room-backdrop';
      backdrop.position.set(0, height / 2, -depth / 2 + 0.052);
      group.add(backdrop);
    });
  }

  function colorValue(hex, fallback) {
    const value = String(hex || '').trim();
    if (/^#[0-9a-f]{6}$/i.test(value)) return parseInt(value.slice(1), 16);
    return fallback;
  }

  function normalizeWalls(room, width, depth, fallbackColor) {
    const records = Array.isArray(room && room.walls) ? room.walls : [];
    const aliases = {
      north: 'back', back: 'back',
      south: 'front', front: 'front',
      west: 'left', left: 'left',
      east: 'right', right: 'right'
    };
    const base = ['back', 'right', 'front', 'left'].map((name) => ({ name, color: fallbackColor }));
    records.forEach((record) => {
      const name = aliases[normalizeText(record.id || record.name)] || aliases[normalizeText(record.wall)];
      const target = base.find((item) => item.name === name);
      if (target) {
        target.color = colorValue(record.color_hex, target.color);
        target.material = record.material || 'paint';
        target.features = record.features || [];
      }
    });
    return base;
  }

  function addWall(group, wall, width, depth, height) {
    const THREE = window.THREE;
    if (wall.name === 'front') return;
    const isCutaway = wall.name === 'front';
    const mat = new THREE.MeshStandardMaterial({
      color: wall.color,
      roughness: 0.82,
      transparent: isCutaway,
      opacity: isCutaway ? 0.12 : 1
    });
    const isSide = wall.name === 'left' || wall.name === 'right';
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(isSide ? 0.045 : width, height, isSide ? depth : 0.045), mat);
    if (wall.name === 'back') mesh.position.set(0, height / 2, -depth / 2);
    if (wall.name === 'front') mesh.position.set(0, height / 2, depth / 2);
    if (wall.name === 'left') mesh.position.set(-width / 2, height / 2, 0);
    if (wall.name === 'right') mesh.position.set(width / 2, height / 2, 0);
    mesh.receiveShadow = true;
    mesh.name = `wall-${wall.name}`;
    group.add(mesh);
  }

  function wallName(value) {
    const text = normalizeText(value);
    if (text.includes('east') || text.includes('right')) return 'right';
    if (text.includes('west') || text.includes('left')) return 'left';
    if (text.includes('south') || text.includes('front')) return 'front';
    return 'back';
  }

  function wallPlacement(wall, xOffset, width, depth, y) {
    if (wall === 'back') return { position: [xOffset, y, -depth / 2 + 0.028], rotation: [0, 0, 0] };
    if (wall === 'front') return { position: [-xOffset, y, depth / 2 - 0.028], rotation: [0, Math.PI, 0] };
    if (wall === 'left') return { position: [-width / 2 + 0.028, y, -xOffset], rotation: [0, Math.PI / 2, 0] };
    return { position: [width / 2 - 0.028, y, xOffset], rotation: [0, -Math.PI / 2, 0] };
  }

  function addOpening(group, opening, width, depth, height) {
    const THREE = window.THREE;
    const type = normalizeText(opening.type || 'window');
    const wall = wallName(opening.wall);
    const ow = feetToScene(opening.width_ft || (type.includes('sliding') ? 6 : type.includes('arch') ? 5 : 4));
    const oh = feetToScene(opening.height_ft || (type.includes('arch') ? 7.2 : type.includes('door') ? 6.8 : 4));
    const bottom = feetToScene(opening.bottom_ft == null ? (type.includes('window') ? 3 : 0.05) : opening.bottom_ft);
    const xOffset = feetToScene(opening.x_ft || opening.offset_ft || 0);
    const placement = wallPlacement(wall, xOffset, width, depth, bottom + oh / 2);
    const glass = type.includes('window') || type.includes('sliding') || opening.glass;
    const panelMat = new THREE.MeshStandardMaterial({
      color: glass ? 0xb8d7e8 : 0x2f2b27,
      roughness: glass ? 0.06 : 0.7,
      metalness: glass ? 0.02 : 0,
      transparent: true,
      opacity: type.includes('arch') || type.includes('opening') ? 0.72 : glass ? 0.42 : 0.92
    });
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(ow, oh), panelMat);
    panel.position.set(...placement.position);
    panel.rotation.set(...placement.rotation);
    panel.name = `opening-${type}`;
    group.add(panel);

    addOpeningFrame(group, placement, ow, oh, colorValue(opening.frame_color_hex, 0xf0ede5));
    if (glass) addExteriorView(group, opening, placement, ow, oh);
    if (type.includes('sliding')) addVerticalBlinds(group, opening, placement, ow, oh);
    if ((opening.treatment || {}).type && normalizeText(opening.treatment.type).includes('curtain')) addCurtains(group, opening, placement, ow, oh);
  }

  function addOpeningFrame(group, placement, ow, oh, color) {
    const THREE = window.THREE;
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.65 });
    const parts = [
      [ow + 0.08, 0.035, 0, oh / 2 + 0.02],
      [ow + 0.08, 0.035, 0, -oh / 2 - 0.02],
      [0.035, oh + 0.08, -ow / 2 - 0.02, 0],
      [0.035, oh + 0.08, ow / 2 + 0.02, 0]
    ];
    parts.forEach(([pw, ph, ox, oy]) => {
      const part = new THREE.Mesh(new THREE.PlaneGeometry(pw, ph), mat);
      part.position.set(...placement.position);
      part.rotation.set(...placement.rotation);
      part.translateX(ox);
      part.translateY(oy);
      group.add(part);
    });
  }

  function addExteriorView(group, opening, placement, ow, oh) {
    const THREE = window.THREE;
    const view = opening.exterior_view || {};
    const colors = Array.isArray(view.colors) ? view.colors : [];
    const sky = colorValue(colors[0], normalizeText(view.type).includes('night') ? 0x10131d : 0x9fc7e5);
    const ground = colorValue(colors[1], normalizeText(view.type).includes('brick') ? 0x9b5d42 : 0x668f5a);
    const skyPanel = new THREE.Mesh(new THREE.PlaneGeometry(ow * 0.92, oh * 0.52), new THREE.MeshBasicMaterial({ color: sky }));
    skyPanel.position.set(...placement.position);
    skyPanel.rotation.set(...placement.rotation);
    skyPanel.translateZ(-0.012);
    skyPanel.translateY(oh * 0.18);
    group.add(skyPanel);
    const groundPanel = new THREE.Mesh(new THREE.PlaneGeometry(ow * 0.92, oh * 0.42), new THREE.MeshBasicMaterial({ color: ground }));
    groundPanel.position.set(...placement.position);
    groundPanel.rotation.set(...placement.rotation);
    groundPanel.translateZ(-0.013);
    groundPanel.translateY(-oh * 0.2);
    group.add(groundPanel);
  }

  function addVerticalBlinds(group, opening, placement, ow, oh) {
    const THREE = window.THREE;
    const color = colorValue((opening.treatment || {}).color_hex, 0xe4dfd2);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.86, transparent: true, opacity: 0.9 });
    const count = 9;
    for (let i = 0; i < count; i += 1) {
      const blind = new THREE.Mesh(new THREE.PlaneGeometry(0.035, oh * 0.92), mat);
      blind.position.set(...placement.position);
      blind.rotation.set(...placement.rotation);
      const sideBias = (opening.treatment || {}).position === 'drawn_back' ? -0.38 : -0.45 + (i / (count - 1)) * 0.9;
      blind.translateX(sideBias * ow);
      blind.translateZ(0.018);
      group.add(blind);
    }
  }

  function addCurtains(group, opening, placement, ow, oh) {
    const THREE = window.THREE;
    const color = colorValue((opening.treatment || {}).color_hex, 0xcbbda5);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.92 });
    [-1, 1].forEach((side) => {
      const panel = new THREE.Mesh(new THREE.BoxGeometry(ow * 0.16, oh * 1.08, 0.035), mat);
      panel.position.set(...placement.position);
      panel.rotation.set(...placement.rotation);
      panel.translateX(side * ow * 0.57);
      panel.translateZ(0.03);
      panel.castShadow = true;
      group.add(panel);
    });
  }

  function addBaseboards(group, walls, width, depth, trim) {
    const THREE = window.THREE;
    const base = trim && trim.baseboards ? trim.baseboards : {};
    if (base.present === false) return;
    const mat = new THREE.MeshStandardMaterial({ color: colorValue(base.color_hex, 0xf2efe8), roughness: 0.55 });
    const h = feetToScene((base.height_in || 4) / 12);
    [
      { geom: [width, h, 0.035], pos: [0, h / 2, -depth / 2 + 0.035] },
      { geom: [width, h, 0.035], pos: [0, h / 2, depth / 2 - 0.035] },
      { geom: [0.035, h, depth], pos: [-width / 2 + 0.035, h / 2, 0] },
      { geom: [0.035, h, depth], pos: [width / 2 - 0.035, h / 2, 0] }
    ].forEach((part) => {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(...part.geom), mat);
      mesh.position.set(...part.pos);
      group.add(mesh);
    });
  }

  function addFloorTexture(group, width, depth, room) {
    const THREE = window.THREE;
    const floorText = normalizeText([room && room.floor_type, room && room.floor && room.floor.texture, room && room.floor && room.floor.material].join(' '));
    const lineMat = new THREE.LineBasicMaterial({ color: floorText.includes('carpet') ? 0xbdb5a7 : 0x6f563b, transparent: true, opacity: floorText.includes('carpet') ? 0.16 : 0.28 });
    const count = floorText.includes('tile') ? 8 : floorText.includes('carpet') ? 20 : 10;
    for (let i = 1; i < count; i += 1) {
      const z = -depth / 2 + (depth / count) * i;
      const points = [new THREE.Vector3(-width / 2, 0.01, z), new THREE.Vector3(width / 2, 0.01, z)];
      group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), lineMat));
    }
  }

  function addCeilingFeatures(group, room, width, depth, height) {
    const THREE = window.THREE;
    const fixtures = []
      .concat((room && room.ceiling && room.ceiling.fixtures) || [])
      .concat((room && room.lighting_fixtures) || []);
    const mat = new THREE.MeshStandardMaterial({ color: 0x2c2c2c, roughness: 0.48, metalness: 0.2 });
    fixtures.slice(0, 8).forEach((fixture, index) => {
      const x = feetToScene(fixture.x_ft || ((index % 3) - 1) * 3);
      const z = feetToScene(fixture.z_ft || (Math.floor(index / 3) - 0.5) * 4);
      const track = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.035, 0.045), mat);
      track.position.set(Math.max(-width / 2 + 0.4, Math.min(width / 2 - 0.4, x)), height - 0.04, Math.max(-depth / 2 + 0.4, Math.min(depth / 2 - 0.4, z)));
      group.add(track);
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.055, 12, 8), new THREE.MeshStandardMaterial({ color: 0xf8f2dc, roughness: 0.25 }));
      head.position.set(track.position.x + 0.18, height - 0.09, track.position.z);
      group.add(head);
    });
  }

  function floorColorFor(value) {
    const text = normalizeText(value);
    if (text.includes('carpet')) return 0xd8d2c6;
    if (text.includes('tile') || text.includes('marble')) return 0xe9e7e1;
    if (text.includes('concrete')) return 0xb8b6ae;
    return 0x9f7a55;
  }

  function wallColorFor(palette) {
    const values = Array.isArray(palette) ? palette.join(' ') : String(palette || '');
    const text = normalizeText(values);
    if (text.includes('cream') || text.includes('beige')) return 0xf2eadc;
    if (text.includes('gray') || text.includes('grey')) return 0xe3e3df;
    if (text.includes('green')) return 0xdfe8dc;
    if (text.includes('blue')) return 0xdfe7ef;
    return 0xf7f2e8;
  }

  function addGridLines(group, width, depth) {
    const THREE = window.THREE;
    const helper = new THREE.GridHelper(Math.max(width, depth), 12, 0x9b8a72, 0xd8d0c4);
    helper.position.y = 0.006;
    helper.material.opacity = 0.35;
    helper.material.transparent = true;
    group.add(helper);
  }

  function productBoxSize(product) {
    const kind = normalizeText(product && product.kind);
    if (kind.includes('sectional')) return { w: 2.8, h: 0.8, d: 1.55 };
    if (kind.includes('sofa')) return { w: 2.25, h: 0.78, d: 0.98 };
    if (kind.includes('chair')) return { w: 0.78, h: 0.86, d: 0.82 };
    if (kind.includes('coffee')) return { w: 1.25, h: 0.38, d: 0.72 };
    if (kind.includes('nightstand')) return { w: 0.62, h: 0.62, d: 0.48 };
    if (kind.includes('bed')) return { w: 1.95, h: 0.72, d: 2.2 };
    return { w: 1, h: 0.72, d: 0.72 };
  }

  function createProductMesh(product) {
    if (!window.THREE) return null;
    const THREE = window.THREE;
    const size = productBoxSize(product);
    const group = new THREE.Group();
    group.userData = { type: 'product', product };
    const baseColor = inferHexColor(((product.colors || [])[0]) || product.title || '');
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(size.w, size.h, size.d),
      new THREE.MeshStandardMaterial({ color: new THREE.Color(baseColor), roughness: 0.58, metalness: 0.03 })
    );
    body.position.y = size.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);
    const edge = new THREE.LineSegments(
      new THREE.EdgesGeometry(body.geometry),
      new THREE.LineBasicMaterial({ color: 0x2c2c2c, transparent: true, opacity: 0.22 })
    );
    edge.position.copy(body.position);
    group.add(edge);
    group.userData.size = size;
    return group;
  }

  function createHumanMesh(person) {
    if (!window.THREE) return null;
    const THREE = window.THREE;
    const heightIn = person.heightUnit === 'cm' ? Number(person.height || 172) / 2.54 : Number(person.height || 68);
    const height = heightIn * 0.0254;
    const weightLb = person.weightUnit === 'kg' ? Number(person.weight || 75) * 2.20462 : Number(person.weight || 165);
    const head = height / 7.5;
    const shoulderWidth = Math.max(head * 1.75, Math.min(head * 2.65, head * (1.95 + (weightLb - 150) / 220)));
    const limb = head * 0.26;
    const group = new THREE.Group();
    group.userData = { type: 'human', person };
    const clothPalette = [0x5d6264, 0x6f675e, 0x4f5961, 0x6b6257, 0x535353];
    const basis = String(person.name || person.label || 'guest').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    const shirtMat = new THREE.MeshStandardMaterial({ color: clothPalette[basis % clothPalette.length], roughness: 0.78 });
    const pantMat = new THREE.MeshStandardMaterial({ color: 0x343434, roughness: 0.82 });
    const skinMat = new THREE.MeshStandardMaterial({ color: 0xd7b99c, roughness: 0.72 });

    function capsule(radius, length, mat, pos, rot) {
      const mesh = new THREE.Mesh(new THREE.CapsuleGeometry(radius, Math.max(0.01, length), 8, 16), mat);
      mesh.position.set(...pos);
      mesh.rotation.set(...(rot || [0, 0, 0]));
      mesh.castShadow = true;
      group.add(mesh);
      return mesh;
    }

    const sitting = person.pose === 'sitting';
    const hipY = sitting ? height * 0.28 : height * 0.46;
    const shoulderY = sitting ? height * 0.58 : height * 0.76;
    const torsoLength = shoulderY - hipY;
    capsule(shoulderWidth * 0.26, torsoLength, shirtMat, [0, hipY + torsoLength / 2, 0], [0, 0, 0]);
    capsule(head * 0.18, head * 0.18, skinMat, [0, shoulderY + head * 0.18, 0], [0, 0, 0]);
    const headMesh = new THREE.Mesh(new THREE.SphereGeometry(head * 0.48, 18, 18), skinMat);
    headMesh.position.y = sitting ? height * 0.74 : height - head * 0.48;
    headMesh.castShadow = true;
    group.add(headMesh);

    [-1, 1].forEach((side) => {
      const swing = person.pose === 'walking' ? side * 0.28 : 0;
      capsule(limb * 0.55, height * 0.18, shirtMat, [side * shoulderWidth * 0.62, shoulderY - height * 0.09, swing], [0.08, 0, side * 0.18]);
      capsule(limb * 0.48, height * 0.17, skinMat, [side * shoulderWidth * 0.68, shoulderY - height * 0.25, -swing * 0.35], [0.1, 0, side * 0.08]);
      const hand = new THREE.Mesh(new THREE.SphereGeometry(limb * 0.58, 12, 10), skinMat);
      hand.position.set(side * shoulderWidth * 0.7, shoulderY - height * 0.36, -swing * 0.4);
      hand.castShadow = true;
      group.add(hand);
    });

    if (person.pose === 'sitting') {
      [-1, 1].forEach((side) => {
        capsule(limb * 0.72, height * 0.21, pantMat, [side * shoulderWidth * 0.24, height * 0.26, head * 0.9], [Math.PI / 2, 0, 0]);
        capsule(limb * 0.62, height * 0.21, pantMat, [side * shoulderWidth * 0.24, height * 0.13, head * 1.65], [0.08, 0, 0]);
        const foot = new THREE.Mesh(new THREE.BoxGeometry(head * 0.34, head * 0.14, head * 0.82), pantMat);
        foot.position.set(side * shoulderWidth * 0.24, head * 0.07, head * 1.92);
        foot.castShadow = true;
        group.add(foot);
      });
    } else {
      [-1, 1].forEach((side) => {
        const stride = person.pose === 'walking' ? side * 0.12 : 0;
        capsule(limb * 0.72, height * 0.22, pantMat, [side * shoulderWidth * 0.18, height * 0.34, stride], [side * 0.08, 0, 0]);
        capsule(limb * 0.62, height * 0.24, pantMat, [side * shoulderWidth * 0.18, height * 0.13, -stride], [-side * 0.08, 0, 0]);
        const foot = new THREE.Mesh(new THREE.BoxGeometry(head * 0.34, head * 0.14, head * 0.82), pantMat);
        foot.position.set(side * shoulderWidth * 0.18, head * 0.07, head * 0.18 + stride);
        foot.castShadow = true;
        group.add(foot);
      });
    }
    group.userData.size = { w: shoulderWidth, h: height, d: head * 1.1 };
    return group;
  }

  function applyRoomLighting(state, room, mode) {
    if (!window.THREE || !state || !state.scene) return;
    const THREE = window.THREE;
    const openings = Array.isArray(room && room.openings) ? room.openings.filter((item) => item.glass || /window|sliding/i.test(item.type || '')) : [];
    const hasExterior = openings.length > 0;
    const preset = ({
      morning: { color: 0xdff0ff, intensity: hasExterior ? 1.55 : 0, hemi: 0.75, exterior: 0xbdd7f0 },
      afternoon: { color: 0xffffff, intensity: hasExterior ? 1.9 : 0, hemi: 0.9, exterior: 0x9fc7e5 },
      evening: { color: 0xffc47e, intensity: hasExterior ? 1.2 : 0, hemi: 0.55, exterior: 0xe7a863 },
      night: { color: 0x7989b8, intensity: hasExterior ? 0.18 : 0, hemi: 0.28, exterior: 0x10131d },
      overhead: { color: 0xffffff, intensity: 1.25, hemi: 0.8 },
      warm: { color: 0xffd3a1, intensity: 1.15, hemi: 0.7 },
      cool: { color: 0xdbe9ff, intensity: 1.2, hemi: 0.75 },
      accent: { color: 0xffc987, intensity: 0.85, hemi: 0.45 }
    })[mode] || { color: 0xffffff, intensity: 1.1, hemi: 0.7 };
    state.scene.traverse((object) => {
      if (object.isHemisphereLight) object.intensity = preset.hemi;
      if (object.isDirectionalLight) {
        object.intensity = preset.intensity;
        object.color.setHex(preset.color);
        const first = openings[0];
        if (first) {
          const wall = wallName(first.wall);
          const x = feetToScene(first.x_ft || 0);
          const y = state.height * 0.72;
          if (wall === 'back') object.position.set(x, y, -state.depth * 0.72);
          else if (wall === 'front') object.position.set(-x, y, state.depth * 0.72);
          else if (wall === 'left') object.position.set(-state.width * 0.72, y, -x);
          else object.position.set(state.width * 0.72, y, x);
        } else {
          object.position.set(0, state.height * 1.35, 0);
        }
      }
    });
    state.roomGroup.traverse((object) => {
      if (object.name && object.name.startsWith('opening-') && object.material && preset.exterior && mode === 'night') {
        object.material.opacity = object.material.opacity ? Math.min(object.material.opacity, 0.28) : 0.28;
      }
    });
  }

  window.Lumira3DEngine = {
    parseConfig,
    loadModelViewer,
    collectColorOptions,
    collectPieceOptions,
    findVariant,
    applyModelColor,
    applyLighting,
    setBackground,
    isMobileARDevice,
    renderQrFallback,
    zoom,
    feetToScene,
    createComposerScene,
    buildRoomShell,
    createProductMesh,
    createHumanMesh,
    applyRoomLighting,
    inferHexColor
  };
})();
